/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Chip from "@material-ui/core/Chip";

import { useTheme } from "@material-ui/core/styles";

import styled from "styled-components";

import { useHistory } from "react-router-dom";

import CoveredRoutesRow from "../components/CoveredRoutesRow";
import RouteDetails from "../containers/RouteDetails";
import AlertsIcon from "../components/common/AlertsIcon";
import TrackingNotesIcon from "../components/common/TrackingNotesIcon";
import WatchedIcon from "../components/common/WatchedIcon";
import StyledTableCellPinned from "../components/common/StyledTableCellPinned";
import StyledOverlay from "../components/common/StyledOverlay";
import StyledNoResults from "../components/common/StyledNoResults";
import RightDrawer from "./RightDrawer";

import { fetchCoveredRoutes } from "../slices/coveredRoutesSlice";
import { updateFilters } from "../slices/filtersSlice";

const StyledTablePagination = styled(TablePagination)`
  flex: 0 0 52px;
  border-top: 2px solid ${(props) => props.theme.palette.background.lightGrey} !important;
  border-bottom: 2px solid ${(props) => props.theme.palette.grey[300]} !important;
  overflow: hidden !important;
`;

const StyledTableRow = styled(TableRow)`
  .sticky {
    position: sticky !important;
    top: 0;
    z-index: 90;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  flex: 1 0 auto;
  height: 0px;
`;

const StyledTableCell = styled(TableCell)`
  border-bottom: none !important;
`;

const GOTRACK_LIMIT = window.env.GOTRACK_LIMIT;
const AUTO_REFRESH_GOTRACK = window.env.AUTO_REFRESH_GOTRACK;

const sortableColumns = [
  "RouteCode",
  "TrailerNumber",
  "ServiceLevel",
  "Miles",
  "Notes",
  "Watched",
  "CarrierName",
  "OriginFacilityAlpha",
  "DestinationFacilityAlpha",
  "StartTimeLate",
  "LastContact",
  "EndTimeLate",
  "NextLegLate",
  "UpsRouteId",
];

let autoRefreshTimeout;

const CoveredRoutesTable = () => {
  const theme = useTheme();
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const coveredRoutes = useSelector(
    (state) => state.coveredRoutes.coveredRoutes
  );

  const filters = useSelector((state) => state.filters);
  const totalCount = useSelector((state) => state.coveredRoutes.totalCount);
  const isFetching = useSelector((state) => state.coveredRoutes.isFetching);
  const currentPage = useSelector((state) => state.filters.currentPage);
  const sessionHasEnded = useSelector(
    (state) => state.goTrackUser.sessionHasEnded
  );
  const apiErrorHasOccurred = useSelector(
    (state) => state.goTrackUser.apiErrorHasOccurred
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const [openedRoute, setOpenedRoute] = useState(null);

  const rowsPerPage = parseInt(GOTRACK_LIMIT, 10);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    dispatch(fetchCoveredRoutes(filters));
  }, [filters]);

  const handleChangePage = (event, newPage) => {
    dispatch(updateFilters({ filters: { currentPage: newPage }, history }));
  };

  const handleSort = (sortColumn) => {
    let updatedFilters;
    if (filters.sortColumn !== sortColumn) {
      updatedFilters = { sortColumn, sortDirection: "Ascending" };
    } else if (filters.sortColumn === sortColumn) {
      if (filters.sortDirection === "Ascending") {
        updatedFilters = { sortColumn, sortDirection: "Descending" };
      } else {
        updatedFilters = { sortColumn: null, sortDirection: null };
      }
    }

    dispatch(
      updateFilters({
        filters: { ...updatedFilters },
        history,
      })
    );
  };

  useEffect(() => {
    if (autoRefreshTimeout) {
      clearTimeout(autoRefreshTimeout);
    }
    if (!openedRoute && !sessionHasEnded && !apiErrorHasOccurred) {
      autoRefreshTimeout = setTimeout(() => {
        dispatch(
          updateFilters({
            filters,
            history,
          })
        );
      }, AUTO_REFRESH_GOTRACK);
    }

    return () => {
      if (autoRefreshTimeout) {
        clearTimeout(autoRefreshTimeout);
      }
    };
  }, [openedRoute, filters, sessionHasEnded, apiErrorHasOccurred]);

  return (
    <>
      {isFetching && <StyledOverlay theme={theme} />}

      {openedRoute && (
        <RightDrawer width={675}>
          <RouteDetails
            openedRoute={openedRoute}
            setOpenedRoute={setOpenedRoute}
            setUnsavedModalOpen={setUnsavedModalOpen}
            setUnsavedChanges={setUnsavedChanges}
            unsavedChanges={unsavedChanges}
            unsavedModalOpen={unsavedModalOpen}
          />
        </RightDrawer>
      )}

      {coveredRoutes.length > 0 ? (
        <>
          <StyledTableContainer theme={theme}>
            <Table
              stickyHeader
              style={{ tableLayout: "fixed" }}
              aria-label="sticky table"
            >
              <TableHead>
                <StyledTableRow>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="upsrouteid"
                    className="sticky upsRouteId"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[13]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[13])}
                      data-testid="ups-route-id"
                    >
                      Route Id
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="code"
                    className="sticky code"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[0]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[0])}
                      data-testid="route-column"
                    >
                      Code
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="legNumber"
                    className="sticky leg"
                    size="small"
                  >
                    Leg #
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="trailer"
                    className="sticky trailer"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[1]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[1])}
                      data-testid="trailer-column"
                    >
                      Trailer
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="srv"
                    className="sticky srv"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[2]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[2])}
                      data-testid="srv-column"
                    >
                      Srv
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="right"
                    key="miles"
                    className="sticky miles"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[3]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[3])}
                      data-testid="miles-column"
                    >
                      Miles
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="notes"
                    className="small-cell sticky notes"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[4]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[4])}
                      data-testid="notes-column"
                    >
                      <TrackingNotesIcon color={theme.palette.grey[500]} />
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="alerts"
                    className="small-cell sticky alerts"
                    size="small"
                  >
                    <AlertsIcon
                      color={theme.palette.grey[500]}
                      style={{ verticalAlign: "middle" }}
                    />
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="watched"
                    className="small-cell sticky watched"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[5]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[5])}
                      data-testid="watched-column"
                    >
                      <WatchedIcon color={theme.palette.grey[500]} />
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCellPinned
                    theme={theme}
                    align="left"
                    key="carrier"
                    className="sticky carrier"
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[6]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[6])}
                      data-testid="carrier-column"
                    >
                      Carrier
                    </TableSortLabel>
                  </StyledTableCellPinned>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="origin"
                    style={{ width: "115px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[7]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[7])}
                      data-testid="origin-column"
                    >
                      Origin
                    </TableSortLabel>
                    {filters.sortColumn === sortableColumns[7] && (
                      <Chip label="Mvmnt" />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="destination"
                    style={{ width: "115px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[8]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[8])}
                      data-testid="destination-column"
                    >
                      Destination
                    </TableSortLabel>
                    {filters.sortColumn === sortableColumns[8] && (
                      <Chip label="Mvmnt" />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="start"
                    style={{ width: "345px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[9]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[9])}
                      data-testid="start-column"
                    >
                      Start
                    </TableSortLabel>
                    {filters.sortColumn === sortableColumns[9] && (
                      <Chip label="Late" />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="lastContact"
                    style={{ width: "345px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[10]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[10])}
                      data-testid="last-contact-column"
                    >
                      Last Contact
                    </TableSortLabel>
                    {filters.sortColumn === sortableColumns[10] && (
                      <Chip label="Silent" />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="end"
                    style={{ width: "345px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[11]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[11])}
                      data-testid="end-column"
                    >
                      End
                    </TableSortLabel>
                    {filters.sortColumn === sortableColumns[11] && (
                      <Chip label="Late" />
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="sort"
                    style={{ width: "220px" }}
                    size="small"
                  >
                    Sort
                  </StyledTableCell>
                  <StyledTableCell
                    theme={theme}
                    align="left"
                    key="nextLeg"
                    style={{ width: "345px" }}
                    size="small"
                  >
                    <TableSortLabel
                      active={filters.sortColumn === sortableColumns[12]}
                      direction={
                        filters.sortDirection !== "Descending" ? "asc" : "desc"
                      }
                      onClick={() => handleSort(sortableColumns[12])}
                      data-testid="next-leg-column"
                    >
                      Next Leg
                    </TableSortLabel>
                    {filters.sortColumn === sortableColumns[12] && (
                      <Chip label="Late" />
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {coveredRoutes.map((row, index) => {
                  return (
                    <CoveredRoutesRow
                      row={row}
                      key={`${row.id}-${row.activeLeg.legSequence}-${index}`}
                      selected={
                        row.id === openedRoute?.id &&
                        row.currentLeg === openedRoute?.currentLeg
                      }
                      setOpenedRoute={setOpenedRoute}
                      setUnsavedModalOpen={setUnsavedModalOpen}
                      unsavedChanges={unsavedChanges}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
          {!isFetching && (
            <StyledTablePagination
              component="div"
              theme={theme}
              count={totalCount}
              rowsPerPageOptions={[rowsPerPage]}
              rowsPerPage={rowsPerPage}
              page={currentPage}
              onChangePage={handleChangePage}
            />
          )}{" "}
        </>
      ) : (
        <StyledNoResults theme={theme}>
          {!isFetching && "No Results"}
        </StyledNoResults>
      )}
    </>
  );
};

export default CoveredRoutesTable;
